import { Component, computed } from '@angular/core';

import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { Router } from '@angular/router';
import { AuthViewModel } from '../../../features/auth/viewmodels/auth.viewmodel';

interface SidebarMenuItem {
  label: string;
  icon: string;
  route?: string;
  enabled: boolean;
}

interface UserDetail {
  label: string;
  value: string;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [ MatSidenavModule, MatIconModule, MatButtonModule, MatTooltipModule, MatCardModule]
})
export class SidebarComponent {

  menuItems: SidebarMenuItem[] = [
    { label: 'Financiamento', icon: 'home_work', route: '/dashboard', enabled: true },
    { label: 'Simulador', icon: 'calculate', route: '/simulador', enabled: true },
    { label: 'Laboratório IA', icon: 'science', route: '/analise-documental/laboratorio', enabled: true },
    { label: 'FGTS', icon: 'account_balance', route: '/fgts', enabled: true }
  ];

  selectedItem = 'Financiamento';

  // Computed signals para dados do usuário
  readonly userFirstName = computed(() => {
    // Aguarda inicialização do MSAL
    if (!this.authViewModel.isInitialized() || !this.authViewModel.isAuthenticated()) {
      return 'Carregando...';
    }

    const user = this.authViewModel.user();
    if (!user?.profile?.firstName) {
      return user?.name?.split(' ')[0] || 'Usuário';
    }
    return user.profile.firstName;
  });

  readonly userInitials = computed(() => {
    // Aguarda inicialização do MSAL
    if (!this.authViewModel.isInitialized() || !this.authViewModel.isAuthenticated()) {
      return '...';
    }

    const user = this.authViewModel.user();
    if (!user?.profile) {
      const name = user?.name || 'U';
      const parts = name.split(' ');
      return parts.length > 1
        ? `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase()
        : name.charAt(0).toUpperCase();
    }

    const firstName = user.profile.firstName || '';
    const lastName = user.profile.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  });

  readonly userDetails = computed<UserDetail[]>(() => {
    const user = this.authViewModel.user();
    if (!user) return [];

    return [
      this.detail('Nome', user.profile?.displayName || user.displayName || user.name),
      this.detail('E-mail', user.mail || user.email),
      this.detail('PRV', user.onPremisesSamAccountName || user.samAccountName || user.username),
      this.detail('Cargo', user.profile?.jobTitle || user.jobTitle),
      this.detail('Área', user.profile?.department || user.department),
      this.detail('Núcleo', user.profile?.nucleo || user.nucleo),
      this.detail('Gerência', user.profile?.gerencia || user.gerencia),
      this.detail('Diretoria', user.profile?.diretoria || user.diretoria),
      this.detail('Localidade', user.profile?.officeLocation || user.officeLocation),
      this.detail('Telefone', user.profile?.phoneNumber || user.mobilePhone || user.businessPhones?.[0])
    ].filter((item): item is UserDetail => item !== null);
  });

  userDetailsVisible = false;

  constructor(
    public authViewModel: AuthViewModel,
    private readonly router: Router
  ) {}

  showUserDetails(): void {
    this.userDetailsVisible = true;
  }

  hideUserDetails(): void {
    this.userDetailsVisible = false;
  }

  onUserFocusOut(event: FocusEvent): void {
    const currentTarget = event.currentTarget as HTMLElement;
    if (!currentTarget.contains(event.relatedTarget as Node | null)) {
      this.hideUserDetails();
    }
  }

  private detail(label: string, value: string | null | undefined): UserDetail | null {
    const normalizedValue = value?.trim();
    return normalizedValue ? { label, value: normalizedValue } : null;
  }

  selectItem(item: SidebarMenuItem): void {
    if (!item.enabled) return;

    this.selectedItem = item.label;
    if (item.route) {
      void this.router.navigateByUrl(item.route);
    }
  }
}
